import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { gymApi } from '@/lib/api';
import { Expense } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, Pencil, Trash2, Receipt, DollarSign, Wallet } from 'lucide-react';
import { NoGymSelected } from '@/components/NoGymSelected';

const categories = [
    { id: 'salary', label: 'Salario de Empleados', color: 'bg-blue-500' },
    { id: 'rent', label: 'Alquiler / Hipoteca', color: 'bg-indigo-500' },
    { id: 'utilities', label: 'Servicios (Luz, Agua, etc.)', color: 'bg-yellow-500' },
    { id: 'maintenance', label: 'Mantenimiento', color: 'bg-orange-500' },
    { id: 'supplies', label: 'Suministros / Limpieza', color: 'bg-green-500' },
    { id: 'marketing', label: 'Publicidad / Marketing', color: 'bg-pink-500' },
    { id: 'other', label: 'Otros Gastos', color: 'bg-slate-500' },
];

export default function ExpensesPage() {
    const { gym, hasRole } = useAuth();
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
    const { toast } = useToast();

    const [formData, setFormData] = useState<Partial<Expense>>({
        category: 'other',
        amount: 0,
        description: '',
        date: new Date().toISOString().split('T')[0],
    });

    const fetchExpenses = async () => {
        if (!gym?.gym_id) return;
        try {
            const res = await gymApi.listExpenses(gym.gym_id);
            if (res.success) setExpenses(res.expenses);
        } catch (error) {
            console.error('Failed to fetch expenses:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchExpenses();
    }, [gym?.gym_id]);

    const handleSaveExpense = async () => {
        if (!gym || formData.amount! <= 0) {
            toast({
                title: 'Datos inválidos',
                description: 'Por favor ingresa un monto válido.',
                variant: 'destructive',
            });
            return;
        }

        setIsSubmitting(true);
        try {
            if (editingExpense) {
                const res = await gymApi.updateExpense(editingExpense.id, formData);
                if (res.success) {
                    toast({ title: 'Gasto actualizado', description: 'Los cambios han sido guardados.' });
                }
            } else {
                const res = await gymApi.createExpense({ ...formData, gym_id: gym.gym_id });
                if (res.success) {
                    toast({ title: 'Gasto registrado', description: 'El gasto ha sido guardado exitosamente.' });
                }
            }
            fetchExpenses();
            setIsCreateOpen(false);
            resetForm();
        } catch (error: any) {
            toast({
                title: 'Error',
                description: error.message || 'No se pudo guardar el gasto.',
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteExpense = async (id: string) => {
        if (!window.confirm('¿Estás seguro de eliminar este registro?')) return;
        try {
            const res = await gymApi.deleteExpense(id);
            if (res.success) {
                setExpenses(expenses.filter(e => e.id !== id));
                toast({ title: 'Gasto eliminado', variant: 'destructive' });
            }
        } catch (error) {
            toast({ title: 'Error', description: 'No se pudo eliminar el gasto.', variant: 'destructive' });
        }
    };

    const resetForm = () => {
        setFormData({
            category: 'other',
            amount: 0,
            description: '',
            date: new Date().toISOString().split('T')[0],
        });
        setEditingExpense(null);
    };

    const openEditDialog = (expense: Expense) => {
        setEditingExpense(expense);
        setFormData({
            category: expense.category,
            amount: expense.amount,
            description: expense.description,
            date: expense.date,
        });
        setIsCreateOpen(true);
    };

    const totalMonthly = expenses
        .filter(e => e.date.startsWith(new Date().toISOString().slice(0, 7)))
        .reduce((sum, e) => sum + Number(e.amount), 0);

    if (!gym) return <DashboardLayout title="Control de Gastos"><NoGymSelected /></DashboardLayout>;

    return (
        <DashboardLayout
            title="Gestión de Gastos"
            subtitle={`Control financiero operativo para ${gym.name}`}
            actions={
                <Button variant="gradient" onClick={() => { resetForm(); setIsCreateOpen(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Registrar Gasto
                </Button>
            }
        >
            {/* Resumen */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card className="glass shadow-glow-sm">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                                <Wallet className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total del Mes</p>
                                <p className="text-2xl font-black">${totalMonthly.toLocaleString()}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="glass">
                    <CardContent className="pt-6 font-medium text-sm text-muted-foreground">
                        Los gastos registrados aquí se restan automáticamente de los ingresos brutos en el Dashboard para calcular tu utilidad neta.
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Receipt className="h-5 w-5 text-primary" />
                        Historial de Gastos ({expenses.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 lg:p-6">
                    <div className="hidden lg:block">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Fecha</TableHead>
                                    <TableHead>Categoría</TableHead>
                                    <TableHead>Descripción</TableHead>
                                    <TableHead>Monto</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {expenses.map((expense) => (
                                    <TableRow key={expense.id}>
                                        <TableCell>{new Date(expense.date).toLocaleDateString('es-ES')}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">
                                                {categories.find(c => c.id === expense.category)?.label || expense.category}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="max-w-xs truncate">{expense.description || '-'}</TableCell>
                                        <TableCell className="font-bold text-destructive">
                                            -${Number(expense.amount).toLocaleString()}
                                        </TableCell>
                                        <TableCell className="text-right space-x-2">
                                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(expense)}>
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteExpense(expense.id)}>
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="lg:hidden divide-y divide-border">
                        {expenses.map((expense) => (
                            <div key={expense.id} className="p-4 flex justify-between items-center">
                                <div>
                                    <p className="font-bold">-{Number(expense.amount).toLocaleString()}</p>
                                    <p className="text-xs text-muted-foreground">{categories.find(c => c.id === expense.category)?.label}</p>
                                    <p className="text-xs text-muted-foreground">{new Date(expense.date).toLocaleDateString()}</p>
                                </div>
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(expense)}><Pencil className="h-4 w-4" /></Button>
                                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteExpense(expense.id)}><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {expenses.length === 0 && (
                        <div className="py-12 text-center text-muted-foreground">
                            No hay gastos registrados.
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Dialog Form */}
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>{editingExpense ? 'Editar Gasto' : 'Registrar Nuevo Gasto'}</DialogTitle>
                        <DialogDescription>
                            Completa los detalles del gasto operativo para mantener tus finanzas al día.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                            <Label>Categoría</Label>
                            <Select
                                value={formData.category}
                                onValueChange={(v: any) => setFormData({ ...formData, category: v })}
                                disabled={isSubmitting}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar categoría" />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map((cat) => (
                                        <SelectItem key={cat.id} value={cat.id}>
                                            {cat.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Monto ($)</Label>
                            <Input
                                type="number"
                                value={formData.amount}
                                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                                placeholder="0.00"
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Fecha</Label>
                            <Input
                                type="date"
                                value={formData.date}
                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Descripción / Notas</Label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Ej: Pago de nómina Juan, Recibo de luz..."
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsCreateOpen(false)} disabled={isSubmitting}>Cancelar</Button>
                        <Button variant="gradient" onClick={handleSaveExpense} disabled={isSubmitting}>
                            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Guardar Gasto'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
